import React from 'react';

import { useFormikContext } from 'formik';

import { appReactMemo } from 'hocs';

/**
 * Scrolls (and optionally focuses) the topmost field that has a validation error
 * after a submit attempt. Works for BOTH front-end (Yup / validationSchema) and
 * back-end errors, because both end up in Formik's `errors` state — FE synchronously
 * on submit, BE asynchronously via `helpers.setErrors()` in the mutation `onError`.
 *
 * "Topmost" is resolved by DOM position (smallest `getBoundingClientRect().top`), NOT
 * by the order of keys in the `errors` object — Formik does not guarantee that order
 * matches the visual layout.
 *
 * Field lookup defaults to `[name="<path>"]` (Formik spreads `name` onto the real
 * input), plus a `[name="['<path>']"]` variant for group-edit forms whose fields use
 * Formik bracket-notation names (the rendered DOM `name` keeps the brackets), and a
 * `[data-error-field="<path>"]` fallback for custom fields that don't render a
 * queryable `name` (OTP, rich editor, etc.).
 */

export interface IUseScrollToFirstErrorOptions {
  /** Turn the behavior off without unmounting (e.g. behind a feature flag). */
  enabled?: boolean;
  /** Focus the field after scrolling. Default: true. */
  focusOnError?: boolean;
  /**
   * Resolve a DOM node for a given Formik field path. Override when a form has
   * fields that can't be found by `name` / `data-error-field`.
   */
  resolveElement?: (path: string) => HTMLElement | null;
  /** Passed straight to `Element.scrollIntoView`. */
  scrollOptions?: ScrollIntoViewOptions;
}

const DEFAULT_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'center',
};

/**
 * Flattens Formik's nested errors object into leaf field paths that match the
 * `name` props used on inputs, e.g. `transaction.metadata[0].key`.
 */
const flattenErrorPaths = (errors: unknown, basePath = ''): string[] => {
  if (!errors || typeof errors !== 'object') {
    return basePath ? [basePath] : [];
  }

  if (Array.isArray(errors)) {
    return errors.flatMap((item, index) =>
      flattenErrorPaths(item, `${basePath}[${index}]`),
    );
  }

  return Object.entries(errors as Record<string, unknown>).flatMap(([key, value]) =>
    flattenErrorPaths(value, basePath ? `${basePath}.${key}` : key),
  );
};

const defaultResolveElement = (path: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[name="${path}"], [name="['${path}']"], [data-error-field="${path}"]`,
  );

const findTopmostElement = (
  paths: string[],
  resolveElement: (path: string) => HTMLElement | null,
): HTMLElement | null => {
  let topmost: HTMLElement | null = null;
  let topmostOffset = Number.POSITIVE_INFINITY;

  paths.forEach((path) => {
    const element = resolveElement(path);
    if (!element) {
      return;
    }
    const { top } = element.getBoundingClientRect();
    if (top < topmostOffset) {
      topmostOffset = top;
      topmost = element;
    }
  });

  return topmost;
};

/**
 * Scrolls to and focuses the topmost (by DOM position) field among `paths`. Shared by
 * `useScrollToFirstError` (Formik forms) and non-Formik forms that track their own error
 * state and can't mount `<ScrollToFirstError />` (e.g. the Terminal Links group-edit
 * panel). No-ops when none of the paths resolve to an element.
 */
export const scrollToFirstErrorField = (
  paths: string[],
  {
    focusOnError,
    resolveElement = defaultResolveElement,
    scrollOptions = DEFAULT_SCROLL_OPTIONS,
  }: Pick<
    IUseScrollToFirstErrorOptions,
    'focusOnError' | 'resolveElement' | 'scrollOptions'
  > = {},
): void => {
  const target = findTopmostElement(paths, resolveElement);
  if (!target) {
    return;
  }

  target.scrollIntoView(scrollOptions);
  if (focusOnError) {
    // preventScroll so focus doesn't fight the smooth scroll.
    target.focus({ preventScroll: true });
  }
};

export const useScrollToFirstError = ({
  enabled = true,
  focusOnError,
  resolveElement = defaultResolveElement,
  scrollOptions = DEFAULT_SCROLL_OPTIONS,
}: IUseScrollToFirstErrorOptions = {}) => {
  // `?? {}` guards the case where the hook is mounted in a shared wrapper
  // (e.g. FormLayout) that is ever rendered outside a <Formik> provider —
  // `useFormikContext` returns `undefined` there at runtime despite its types.
  const { errors, isSubmitting, isValidating, submitCount } =
    useFormikContext<Record<string, unknown>>() ?? {};

  // Becomes `true` on a fresh submit attempt and stays armed until we either
  // scroll to an error or the submit settles cleanly (handles async BE errors).
  const armedRef = React.useRef(false);
  const lastSubmitCountRef = React.useRef(submitCount);

  React.useEffect(() => {
    if (submitCount !== lastSubmitCountRef.current) {
      lastSubmitCountRef.current = submitCount;
      armedRef.current = enabled;
    }
  }, [submitCount, enabled]);

  React.useEffect(() => {
    if (!enabled || !armedRef.current || isValidating) {
      return;
    }

    const paths = flattenErrorPaths(errors);

    if (paths.length === 0) {
      // No errors yet. For async BE validation the response hasn't arrived —
      // stay armed until the submit settles, otherwise disarm (clean submit).
      if (!isSubmitting) {
        armedRef.current = false;
      }
      return;
    }

    armedRef.current = false;

    scrollToFirstErrorField(paths, { focusOnError, resolveElement, scrollOptions });
  }, [
    enabled,
    errors,
    focusOnError,
    isSubmitting,
    isValidating,
    resolveElement,
    scrollOptions,
  ]);
};

/**
 * Drop-in component: render once inside any `<Formik>` to enable scroll-to-first-error.
 * Renders nothing.
 */
export const ScrollToFirstError = appReactMemo((props: IUseScrollToFirstErrorOptions) => {
  useScrollToFirstError(props);
  return null;
});
